import { FC, useEffect } from 'react';
import styled from '@emotion/styled';
import { useAtomValue, useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import ImgGuidelineModeration from 'src/assets/images/imgGuidelineModeration.svg';
import ImgGuidelineNoConsent from 'src/assets/images/imgGuidelineNoConsent.svg';
import ImgGuidelineNoEntry from 'src/assets/images/imgGuidelineNoEntry.svg';
import ImgGuidelineSupport from 'src/assets/images/imgGuidelineSupport.svg';
import { Wrap } from 'src/components/AzarOnly/Login/styles';
import ButtonBase from 'src/components/Button';
import CmpConsentsModal from 'src/components/CmpConsentsModal';
import ModalLayoutBase, { ModalFooter as ModalFooterBase, ModalInner } from 'src/components/ModalLayout';
import { ModalHeaderTitle as ModalHeaderTitleBase } from 'src/components/ModalLayout';
import { MobileModalType } from 'src/components/ModalLayout';
import useCmpConsents from 'src/hooks/useCmpConsents';
import useCssMediaDevice from 'src/hooks/useCssMediaDevice';
import useEnableRemoteConfig from 'src/hooks/useEnableRemoteConfig';
import { useGetConsents } from 'src/hooks/useGetConsents';
import { featureSettingsPopupsAtom, tryLoginTypeAtom } from 'src/stores/auth/atoms';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { setFeatureSettingsAtom } from 'src/stores/user/atom';
import { UserSettingType } from 'src/stores/user/types';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalNavButtonType, ModalType } from 'src/types/Modal';
import { pushEvent } from 'src/utils/event-gateway';
const ModalLayout = styled(ModalLayoutBase)`
  width: 500px;
  min-height: 600px;
  height: auto;

  & > ${ModalInner} {
    min-height: inherit;
  }
`;
const ListContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 30px;
`;
const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  :last-child {
    margin-bottom: 0;
  }
  background-color: transparent;
  border-radius: 16px;
`;
const RowImage = styled.svg`
  ${({
  theme
}) => theme.isRtl ? 'margin-left' : 'margin-right'}: 16px;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
`;
const RowRight = styled.div`
  display: flex;
  flex-direction: column;
`;
const RowTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  line-height: 26px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    font-size: 18px;
    line-height: 24px;
  }

  margin-bottom: 4px;
  color: ${({
  theme
}) => theme.color.content12};
`;
const RowDesc = styled.div`
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    font-size: 14px;
    line-height: 18px;
  }

  color: ${({
  theme
}) => theme.color.content10};
`;
const GuideLine = styled.p<{
  isCenter: boolean;
}>`
  display: flex;
  font-size: 12px;
  color: ${({
  theme
}) => theme.color.content8};
  a {
    text-decoration: underline;
    font-weight: 700;
    :visited {
      color: inherit;
    }
  }
  align-items: center;
  ${({
  theme
}) => theme.screenSize.mobile} {
    font-size: 14px;
    color: ${({
  theme
}) => theme.color.content9};
    ${({
  isCenter
}) => isCenter && 'justify-content: center;'}
  }
  white-space: nowrap;
`;
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 40px;
`;
const ModalHeaderTitle = styled(ModalHeaderTitleBase)`
  font-size: 36px;
  font-weight: 700;
  line-height: 40px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    padding: 0;
    line-height: normal;
    ${({
  mobileType,
  theme
}) => mobileType === MobileModalType.SCREEN_MODAL && (theme.isRtl ? 'margin-right' : 'margin-left') + ': 0px;'}
  }
`;
const ModalContent = styled(Wrap)`
  padding: 24px;
`;
const ModalFooter = styled(ModalFooterBase)`
  padding-top: 0;
  gap: 16px;
`;
const AdditionalConsentsWrapper = styled.div`
  border-radius: 6px;
  background: ${({
  theme
}) => theme.color.surface4};
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  ${({
  theme
}) => theme.screenSize.mobile} {
    background: ${({
  theme
}) => theme.color.surface2};
  }
`;
const AdditionalConsentsTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({
  theme
}) => theme.color.content10};

  a {
    text-decoration: underline;
    :visited {
      color: inherit;
    }
  }
`;
const AdditionalConsents = styled.div`
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  word-break: keep-all;
  color: ${({
  theme
}) => theme.color.content8};

  ${({
  theme
}) => theme.screenSize.mobile} {
    font-size: 13px;
    line-height: normal;
  }

  a {
    color: inherit;
    text-decoration: underline;
    font-weight: 700;
    :visited {
      color: inherit;
    }
  }
`;
const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-start;
  gap: 8px;
`;
const Button = styled(ButtonBase)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 95px;
  width: 100%;
`;
const GUIDELINES = [{
  key: 'real_time_ai_moderation_is_on',
  icon: ImgGuidelineModeration,
  title: 'guidelines_list_title_1',
  description: 'guidelines_list_desc_1'
}, {
  key: 'no_consent_strictly_prohibited',
  icon: ImgGuidelineNoConsent,
  title: 'guidelines_list_title_2',
  description: 'guidelines_list_desc_2'
}, {
  key: 'reports_are_reviewed_round_the_clock',
  icon: ImgGuidelineSupport,
  title: 'guidelines_list_title_3',
  description: 'guidelines_list_desc_3'
}, {
  key: 'avoid_showing_content_that_contains_minors',
  icon: ImgGuidelineNoEntry,
  title: 'guidelines_list_title_4',
  description: 'guidelines_list_desc_4'
}] as const;
const RegisterGuidelineModal: FC = () => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const {
    data: consentsData
  } = useGetConsents();
  const {
    guidelines,
    uiType: consentsUiType
  } = consentsData?.data?.result || {};
  const bioMetric = guidelines?.find(({
    type
  }) => type === 'BIOMETRIC');
  const {
    cmpConsents
  } = useCmpConsents();
  const showModal = useSetAtom(showModalAtom);
  const shouldShowLiquiditySharingNotice = useEnableRemoteConfig('showLiquiditySharingNotice');
  const deviceType = useCssMediaDevice();
  const setFeatureSettings = useSetAtom(setFeatureSettingsAtom);
  const setFeatureSettingsPopups = useSetAtom(featureSettingsPopupsAtom);
  const handleClickNext = async () => {
    if (shouldShowLiquiditySharingNotice) {
      setFeatureSettings([{
        type: UserSettingType.LIQUIDITY_SHARING,
        value: true
      }]);
      // 가이드라인에서 이미 동의한 featureSettings(동의팝업)를 useFeatureSettingsPopupModal에서 재노출 하지 않기 위해, 제거처리
      setFeatureSettingsPopups(prev => prev?.filter(({
        type
      }) => type !== UserSettingType.LIQUIDITY_SHARING) || []);
    }
    closeModal(ModalType.REGISTER_GUIDELINE);
  };
  const tryLoginType = useAtomValue(tryLoginTypeAtom);
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.SIGN_UP,
      eventName: EVENT_NAME.SCREEN_OPEN__LOGIN_COMMUNITY,
      eventParams: {
        loginType: tryLoginType
      }
    });
  }, [tryLoginType]);
  useEffect(() => {
    if (cmpConsents) {
      showModal({
        key: ModalType.CMP_CONSENTS,
        component: () => <CmpConsentsModal />
      });
    }
  }, [cmpConsents, showModal]);
  return <ModalLayout isDim closeWhenClickOutside={false} mobileNavButtonType={ModalNavButtonType.NONE} data-testid='RegisterGuidelineModal' data-sentry-element="ModalLayout" data-sentry-component="RegisterGuidelineModal" data-sentry-source-file="GuidelineModal.tsx">
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="GuidelineModal.tsx">
        {/* Overflow시, Header와 Content가 scroll되어야 한다는 디자인 요구사항으로 ModalHeader를 사용하지 않고 Header를 ModalContent에 포함함 */}
        <HeaderWrapper data-sentry-element="HeaderWrapper" data-sentry-source-file="GuidelineModal.tsx">
          <ModalHeaderTitle mobileType={MobileModalType.SCREEN_MODAL} data-sentry-element="ModalHeaderTitle" data-sentry-source-file="GuidelineModal.tsx">
            {t('guidelines_title')}
          </ModalHeaderTitle>
          {deviceType !== 'mobile' && <GuideLine isCenter={consentsUiType === 'CONSENT_A'} dangerouslySetInnerHTML={{
          __html: t('guidelines_link_text')
        }} />}
        </HeaderWrapper>

        <ListContainer data-sentry-element="ListContainer" data-sentry-source-file="GuidelineModal.tsx">
          {GUIDELINES.map(({
          key,
          icon: GuidelineIcon,
          title: titleKey,
          description: descriptionKey
        }) => {
          const Icon = GuidelineIcon;
          return <Row key={key}>
                  <RowImage as={Icon} />
                  <RowRight>
                    <RowTitle>{t(titleKey)}</RowTitle>
                    <RowDesc>{t(descriptionKey)}</RowDesc>
                  </RowRight>
                </Row>;
        })}
        </ListContainer>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="GuidelineModal.tsx">
        {bioMetric && consentsUiType === 'CONSENT_B' && <AdditionalConsentsWrapper data-testid='RegisterGuidelineModal-BiometricsNotice'>
            <AdditionalConsents dangerouslySetInnerHTML={{
          __html: bioMetric.content
        }} />
          </AdditionalConsentsWrapper>}
        {shouldShowLiquiditySharingNotice && <AdditionalConsentsWrapper>
            <AdditionalConsentsTitle>{t('guidelines_liquidity_title')}</AdditionalConsentsTitle>
            <AdditionalConsents dangerouslySetInnerHTML={{
          __html: t('guidelines_liquidity_desc')
        }} />
          </AdditionalConsentsWrapper>}
        <ButtonWrapper data-sentry-element="ButtonWrapper" data-sentry-source-file="GuidelineModal.tsx">
          <Button onClick={handleClickNext} id='guidelines_button' data-testid='RegisterGuidelineStep-button-next' data-sentry-element="Button" data-sentry-source-file="GuidelineModal.tsx">
            {t('guidelines_button')}
          </Button>
        </ButtonWrapper>

        {deviceType === 'mobile' && <GuideLine isCenter={true} dangerouslySetInnerHTML={{
        __html: t('guidelines_link_text')
      }} />}
      </ModalFooter>
    </ModalLayout>;
};
export default RegisterGuidelineModal;